import React, { useState, useEffect } from 'react';
import { Lock, Loader2, Save, Trash2, AtSign, Globe, ShieldCheck, KeyRound, X } from 'lucide-react';
import { InstagramAccount } from '../../types';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { validateInstagramUsername, isValidInstagramChar } from '../../utils/validation';
import { countries } from '../../data/countries';
import { hikerApi } from '../../services/hikerApi';

interface SettingsProps {
  account: InstagramAccount;
  isDarkMode: boolean;
  language: string;
  onAccountDeleted?: () => Promise<void>;
}

export function Settings({ account, isDarkMode, language, onAccountDeleted }: SettingsProps) {
  const navigate = useNavigate();
  const [username] = useState(account.username.replace('@', ''));
  const [password, setPassword] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [country, setCountry] = useState(account.country_code);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'pending' | 'connected' | 'failed' | '2fa'>(
    account.connection_status as any || 'pending'
  );
  const [accountId, setAccountId] = useState<string | null>(account.id);
  const [is2FASubmitting, setIs2FASubmitting] = useState(false);
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    const checkAdminStatus = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      setIsAdmin(profile?.role === 'admin');
    };

    checkAdminStatus();
  }, []);

  // Poll connection status when accountId is available
  useEffect(() => {
    let interval: number;
    
    if (accountId && (connectionStatus === 'pending' || connectionStatus === '2fa')) {
      interval = setInterval(async () => {
        try {
          const { data: account, error } = await supabase
            .from('instagram_accounts')
            .select('*')
            .eq('id', accountId)
            .single();

          if (error) throw error;

          if (account) {
            const newStatus = account.connection_status as 'pending' | 'connected' | 'failed' | '2fa';
            
            // Only update status if it's different and we're not in the middle of 2FA submission
            if (newStatus !== connectionStatus && !(is2FASubmitting && newStatus === 'pending')) {
              setConnectionStatus(newStatus);

              switch (newStatus) {
                case 'connected':
                  clearInterval(interval);
                  navigate('/');
                  break;
                case 'failed':
                  clearInterval(interval);
                  setError(t.invalid_credentials);
                  setShowResetModal(true);
                  setIsLoading(false);
                  setIs2FASubmitting(false);
                  break;
                case '2fa':
                  setIsLoading(false);
                  break;
                case 'pending':
                  // Keep polling
                  break;
              }
            }
          }
        } catch (error) {
          console.error('Error checking status:', error);
        }
      }, 2000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [accountId, connectionStatus, is2FASubmitting]);

  const handleSubmit2FA = async () => {
    if (!twoFactorCode || !accountId) {
      setError(t.two_factor_code_required);
      return;
    }

    setIs2FASubmitting(true);
    setError(null);

    try {
      // Send 2FA code to Make webhook - using the same webhook as AddAccount
      const response = await fetch('https://hook.eu1.make.com/u3h1olfqcy9znpjicvgve7r9mxsryo9d', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountId,
          twoFactorCode
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to verify 2FA code');
      }

      // Keep connection status as 2FA while polling
      setConnectionStatus('2fa');
    } catch (error) {
      console.error('Error submitting 2FA:', error);
      setError(error instanceof Error ? error.message : 'Failed to verify 2FA code');
      setIs2FASubmitting(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (!password) {
      setError(t.password_required);
      return;
    }

    // Don't allow updates if already connected
    if (account.connection_status === 'connected') {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Send webhook to Make
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/make-webhook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          username,
          password,
          country,
          accountId: account.id
        }),
      });

      // Update account status
      const { error: updateError } = await supabase
        .from('instagram_accounts')
        .update({ 
          country_code: country,
          connection_status: 'pending',
          connection_updated_at: new Date().toISOString()
        })
        .eq('id', account.id);

      if (updateError) throw updateError;

      setPassword('');
      setError(null);
    } catch (err) {
      console.error('Error updating account:', err);
      setError(err instanceof Error ? err.message : 'Failed to update account. Please try again.');
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!account.id) {
      setError('Invalid account ID');
      setShowDeleteConfirm(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Delete all targets first
      const { error: targetsError } = await supabase
        .from('instagram_targets')
        .delete()
        .eq('account_id', account.id);

      if (targetsError) {
        console.error('Error deleting targets:', targetsError);
        throw new Error('Failed to delete targets');
      }

      // Then delete the account
      const { error: accountError } = await supabase
        .from('instagram_accounts')
        .delete()
        .eq('id', account.id);

      if (accountError) {
        console.error('Error deleting account:', accountError);
        throw new Error('Failed to delete account');
      }

      setShowDeleteConfirm(false);

      // Call onAccountDeleted callback if provided
      if (onAccountDeleted) {
        await onAccountDeleted();
      }

      // Navigate to home page
      navigate('/', { replace: true });
    } catch (err: any) {
      console.error('Error in handleDeleteAccount:', err);
      setError(err.message || t.checkout_error);
    } finally {
      setIsLoading(false);
    }
  };

  const render2FAStep = () => (
    <div>
      <h2 className={`text-xl font-semibold mb-2 ${
        isDarkMode ? 'text-gray-200' : 'text-gray-900'
      }`}>
        {t.two_factor_auth}
      </h2>
      <p className={`text-sm mb-6 whitespace-pre-line ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        {t.two_factor_description}
      </p>
      <div className="relative">
        <input
          type="text"
          value={twoFactorCode}
          onChange={(e) => {
            const value = e.target.value.replace(/\s/g, '');
            setTwoFactorCode(value);
            setError(null);
          }}
          className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
            isDarkMode
              ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
              : 'bg-gray-50 text-gray-900 border-gray-200'
          } focus:outline-none focus:ring-2 focus:ring-orange-500`}
          placeholder="Enter 2FA code"
          required
        />
        <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className={`p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <h2 className={`text-lg font-semibold mb-6 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>{t.account_settings}</h2>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
            {error}
            {connectionStatus === 'failed' && (
              <div className="mt-2">
                <button
                  onClick={() => setShowResetModal(true)}
                  className="text-red-600 hover:text-red-700 underline text-sm font-medium"
                >
                  {t.forgot_password}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          {connectionStatus === '2fa' ? (
            render2FAStep()
          ) : (
            <>
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.username}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={username}
                    readOnly
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm bg-opacity-50 cursor-not-allowed ${
                      isDarkMode 
                        ? 'bg-[#2b2b2b] text-gray-400 border-[#2b2b2b]' 
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                  />
                  <AtSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.password}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError(null);
                    }}
                    disabled={account.connection_status === 'connected'}
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      account.connection_status === 'connected' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    placeholder={t.password_placeholder}
                    required
                  />
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.country}
                </label>
                <div className="relative">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    disabled={account.connection_status === 'connected'}
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm appearance-none ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                      account.connection_status === 'connected' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                  <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end">
            <button
              onClick={connectionStatus === '2fa' ? handleSubmit2FA : handleUpdateAccount}
              disabled={isLoading || is2FASubmitting || account.connection_status === 'connected'}
              className="w-full bg-orange-500 text-white rounded-lg py-2.5 font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading || is2FASubmitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {connectionStatus === '2fa' ? t.verifying : t.connecting}
                </>
              ) : connectionStatus === '2fa' ? (
                t.finalize_connection
              ) : (
                t.connect
              )}
            </button>
          </div>

          {isLoading && !is2FASubmitting && (
            <p className={`mt-4 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t.please_wait_connection}
            </p>
          )}

          {/* Secure connection message */}
          <div className={`mt-6 pt-6 border-t ${
            isDarkMode ? 'border-[#2b2b2b]' : 'border-gray-100'
          }`}>
            <div className={`flex items-center justify-center gap-2 text-sm font-medium ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <ShieldCheck size={16} className="text-emerald-500" strokeWidth={2.5} />
              <span>{t.secure_connection}</span>
            </div>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className={`p-6 rounded-lg border ${
          isDarkMode 
            ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
            : 'bg-white border-[#e5e7eb]'
        }`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-lg font-semibold ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>{t.delete_account_confirm}</h2>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-red-500 hover:text-red-600 transition-colors flex items-center gap-1.5"
            >
              <Trash2 size={14} strokeWidth={2.3} />
              <span className="text-[13px] font-medium">{t.delete}</span>
            </button>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-[400px] rounded-lg p-6 ${
            isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
          }`}>
            <h3 className={`text-lg font-semibold mb-2 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {t.delete_account_confirm}
            </h3>
            <p className={`text-sm mb-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t.delete_account_warning}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setError(null);
                }}
                disabled={isLoading}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  isDarkMode 
                    ? 'text-gray-300 hover:bg-[#2b2b2b]' 
                    : 'text-gray-600 hover:bg-gray-100'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {t.cancel}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    {t.verifying}
                  </>
                ) : (
                  <>
                    <Trash2 size={14} strokeWidth={2.3} />
                    {t.delete}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-lg p-6 ${
            isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-semibold ${
                isDarkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>
                {t.reset_password_title}
              </h3>
              <button 
                onClick={() => setShowResetModal(false)}
                className={`p-1 rounded-full ${
                  isDarkMode ? 'hover:bg-[#2b2b2b]' : 'hover:bg-gray-100'
                }`}
              >
                <X size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </button>
            </div>
            
            <div className={`text-sm mb-6 ${
              isDarkMode ? 'text-gray-300' : 'text-gray-600'
            }`}>
              <p className="mb-4">{t.reset_password_description}</p>
              <p className="font-medium mb-2">{t.reset_password_steps}</p>
              <p className="mb-2">{t.reset_password_step1}</p>
              <p className="mb-2">{t.reset_password_step2}</p>
              <p className="mb-2">{t.reset_password_step3}</p>
              <p className="mb-2 text-red-500 font-medium">{t.reset_password_warning1}</p>
              <p className="mb-2 text-red-500 font-medium">{t.reset_password_warning2}</p>
              <p className="mb-2">{t.reset_password_step4}</p>
              <p className="mb-2">{t.reset_password_step5}</p>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowResetModal(false)}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
              >
                {t.understood}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}